export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Single source of truth for contact-channel validation rules (PODZ-1814).
 * `isValidEmail` trims then matches the shared EMAIL_REGEX; `isValidPhone`
 * strips every non-digit and requires exactly 10 (matching the legacy
 * phone-modal `phoneNumberValid` rule). Defined here so the shared
 * AddressCommsFields editor — and any future consumer — share one definition
 * instead of re-implementing the regex / 10-digit count inline.
 */
export function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value.trim());
}

export function isValidPhone(value: string): boolean {
  return value.replace(/\D/g, '').length === 10;
}

export function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Format integer cents as a USD currency string ($X.XX).
 *
 * Defensive coerce: a missing / null / non-finite wire value renders as
 * "$0.00" instead of "$NaN" (the previous inline copies in
 * QuoteModal.vue, QuoteHeroCard.vue, QuoteLineItemsCard.vue,
 * QuoteTotalsBlock.vue, and FinancingOptionsPanel.vue all open-coded this
 * exact guard — WR-07 from the Phase 5.5 review consolidates them here).
 *
 * Locale + currency are hard-coded to en-US / USD; multi-currency support
 * would add a second parameter once it ships.
 */
export function formatCentsAsUSD(cents: number | undefined | null): string {
  const n = Number(cents);
  const safe = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(safe / 100);
}
